import React, { useState, useEffect, useCallback } from 'react';
import { fetchChatLogs } from '../../services/service';
import FilterPanel from '../filter_Panel/Fillter_Panel';
import { useAsgardeoAuth } from '../../hooks/useAsgardeoAuth';

function ChatLogList({ onSelect, filters = {}, onFilterChange, onClearFilters }) {
  const { getAccessToken, getIDToken, getDecodedIDToken } = useAsgardeoAuth();
  const [chatLogs, setChatLogs] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Pagination state
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [allLogs, setAllLogs] = useState([]);
  const [showFilters, setShowFilters] = useState(false);

  // Helper function to highlight search text
  const highlightText = (text, searchTerm) => {
    if (!searchTerm || !text) return text;
    
    const regex = new RegExp(`(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = text.split(regex);
    
    return parts.map((part, index) => 
      regex.test(part) ? (
        <mark key={index} className="bg-yellow-200 text-black px-1 rounded">
          {part}
        </mark>
      ) : part
    );
  };

  // Helper function to get display text with highlighting
  const getDisplayTextWithHighlight = (text, maxLength = 100) => {
    if (!text) return 'N/A';
    
    const displayText = text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
    
    if (filters.search && filters.search.trim() !== '') {
      return highlightText(displayText, filters.search);
    }
    
    return displayText;
  };

  // Helper function to create date range for single day
  const createSingleDayRange = (dateString) => {
    if (!dateString) return { from: null, to: null };
    
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return { from: null, to: null };
    
    // Create start of day (00:00:00) in local timezone
    const startOfDay = new Date(date);
    startOfDay.setHours(0, 0, 0, 0);
    
    // Create end of day (23:59:59) in local timezone
    const endOfDay = new Date(date);
    endOfDay.setHours(23, 59, 59, 999);
    
    // Format dates in YYYY-MM-DD format
    const formatDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
    
    return {
      from: formatDate(startOfDay),
      to: formatDate(endOfDay)
    };
  };

  // Helper function to get next day for range queries
  const getNextDay = (dateString) => {
    if (!dateString) return null;
    
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return null;
    
    // Add one day
    date.setDate(date.getDate() + 1);
    
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Memoized fetch function for better performance
  const fetchLogs = useCallback(async (isLoadMore = false) => {
    if (isLoadMore) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }
    setError(null);
    
    try {
      const params = {
        page: isLoadMore ? page + 1 : 1,
        limit: filters.limit || 50, // Use the limit from filters
        authContext: { getAccessToken, getIDToken, getDecodedIDToken } // Pass auth context
      };
      
      // Apply filters based on the new logic
      if (filters.recentLogs) {
        // For recent logs, we don't need date parameters
        // The backend should return the most recent logs
        console.log('Fetching recent logs (no date filters)');
      } else if (filters.fromDate && filters.toDate) {
        // If both dates are selected, use the "to date" logic (specific day)
        const singleDayRange = createSingleDayRange(filters.toDate);
        params.from = singleDayRange.from;
        params.to = singleDayRange.to;
        console.log('Both dates selected - using To Date logic:', { from: params.from, to: params.to });
      } else if (filters.fromDate) {
        // If only fromDate is selected, show from that date to today
        params.from = filters.fromDate;
        // Don't set 'to' parameter to let backend handle "to today"
        console.log('From Date only selected:', { from: params.from });
      } else if (filters.toDate) {
        // If only toDate is selected, show only that specific day
        // Use a range from the selected date to the next day (exclusive)
        params.from = filters.toDate;
        params.to = getNextDay(filters.toDate);
        console.log('To Date only selected - range query:', { from: params.from, to: params.to });
      }
      
      console.log('Final API parameters:', params);
      const data = await fetchChatLogs(params);
      
      // If no logs found and it's a single day query, try alternative approach
      if ((data.logs || []).length === 0 && (filters.toDate || (filters.fromDate && filters.toDate))) {
        console.log('No logs found with range query, trying alternative approach...');
        
        // Try with exact date match
        const alternativeParams = {
          page: isLoadMore ? page + 1 : 1,
          limit: filters.limit || 50,
          authContext: { getAccessToken, getIDToken, getDecodedIDToken } // Pass auth context
        };
        
        if (filters.toDate) {
          alternativeParams.from = filters.toDate;
          alternativeParams.to = filters.toDate;
        } else if (filters.fromDate && filters.toDate) {
          alternativeParams.from = filters.toDate;
          alternativeParams.to = filters.toDate;
        }
        
        console.log('Trying alternative parameters:', alternativeParams);
        const alternativeData = await fetchChatLogs(alternativeParams);
        
        if ((alternativeData.logs || []).length > 0) {
          console.log('Alternative approach worked, found logs:', alternativeData.logs.length);
          data.logs = alternativeData.logs;
          data.total = alternativeData.total;
        }
      }
      
      if (isLoadMore) {
        const newLogs = [...allLogs, ...(data.logs || [])];
        setAllLogs(newLogs);
        setChatLogs(newLogs);
        setPage(prev => prev + 1);
        setHasMore((data.logs || []).length === (filters.limit || 50));
      } else {
        setAllLogs(data.logs || []);
        setChatLogs(data.logs || []);
        setPage(1);
        setHasMore((data.logs || []).length === (filters.limit || 50));
      }
      
      console.log('Fetched logs count:', (data.logs || []).length);
    } catch (err) {
      if (err.name === 'AbortError') {
        console.warn('Chat logs request timed out:', err.message);
        setError('Request timed out while loading chat logs. Please try again.');
        return;
      }

      console.error('Failed to fetch chat logs:', err);
      
      // Handle specific error types
      if (err.message.includes('Authentication required')) {
        setError('Please sign in again to view chat logs.');
      } else if (err.message.includes('Access denied')) {
        setError('You do not have permission to view these logs.');
      } else if (err.message.includes('not found')) {
        setError('No chat logs found for the selected criteria.');
      } else if (err.message.includes('Server error')) {
        setError('Server is temporarily unavailable. Please try again later.');
      } else {
        setError('Failed to load chat logs. Please try again.');
      }
    } finally {
      if (isLoadMore) {
        setLoadingMore(false);
      } else {
        setLoading(false);
      }
    }
  }, [page, allLogs, filters, getAccessToken, getIDToken, getDecodedIDToken]);

  // Load more logs function
  const handleLoadMore = () => {
    if (!loadingMore && hasMore) {
      fetchLogs(true);
    }
  };

  useEffect(() => {
    fetchLogs(false);
  }, [filters]);

  // Filter logs based on status and search
  const filteredLogs = chatLogs.filter(log => {
    // First apply status filter
    if (filters.status && filters.status !== 'all') {
      if (log.status !== filters.status) return false;
    }
    
    // Then apply search filter
    if (filters.search && filters.search.trim() !== '') {
      const searchLower = filters.search.toLowerCase();
      return (
        (log.original_query && log.original_query.toLowerCase().includes(searchLower)) ||
        (log.processed_query && log.processed_query.toLowerCase().includes(searchLower)) ||
        (log.final_answer && log.final_answer.toLowerCase().includes(searchLower)) ||
        (log.final_context_for_llm && log.final_context_for_llm.toLowerCase().includes(searchLower)) ||
        (log.tags && log.tags.some(tag => tag.toLowerCase().includes(searchLower))) ||
        // Keep backward compatibility with old field names
        (log.originalQuery && log.originalQuery.toLowerCase().includes(searchLower)) ||
        (log.processedQuery && log.processedQuery.toLowerCase().includes(searchLower)) ||
        (log.finalAnswer && log.finalAnswer.toLowerCase().includes(searchLower))
      );
    }
    
    return true;
  });

  // Get current filter summary
  const getFilterSummary = () => {
    const parts = [];
    
    if (filters.search && filters.search.trim() !== '') {
      parts.push(`Search: "${filters.search}"`);
    }
    
    if (filters.recentLogs) {
      parts.push('Recent logs');
    } else if (filters.fromDate && filters.toDate) {
      parts.push(`Date: ${filters.toDate} (specific day)`);
    } else if (filters.fromDate) {
      parts.push(`From: ${filters.fromDate} to today`);
    } else if (filters.toDate) {
      parts.push(`Date: ${filters.toDate} (specific day)`);
    }
    
    if (filters.status && filters.status !== 'all') {
      parts.push(`Status: ${filters.status}`);
    }
    
    if (filters.limit) {
      parts.push(`Max: ${filters.limit} logs`);
    }
    
    return parts.length > 0 ? parts.join(' • ') : 'All logs';
  };

  return (
    <div className="relative w-full h-full bg-pl_bg_light_4 border-r border-pl_border_light_1 flex flex-col">
      {/* Modern Header */}
      <div className="flex items-center justify-between p-3 sm:p-4 border-b border-pl_border_light_1 bg-pl_bg_light_3">
        <div className="flex-1 min-w-0">
          <h3 className="text-base sm:text-lg lg:text-xl font-bold text-pl_txt_light_1">
            Chat Logs
          </h3>
          <p className="text-xs sm:text-sm text-pl_txt_light_2 truncate mt-1">
            {getFilterSummary()}
          </p>
          {/* Debug info - remove in production */}
          <p className="text-xs text-pl_txt_light_3 mt-1">
            Showing {filteredLogs.length} logs
          </p>
        </div>
        <button
          onClick={() => setShowFilters((prev) => !prev)}
          className="flex items-center gap-1 sm:gap-2 px-2 sm:px-4 py-1.5 sm:py-2 rounded-10 bg-pl_bg_light_5 text-pl_prim_color_1 font-semibold hover:bg-pl_bg_light_4 transition border border-pl_border_light_1 shadow-sm text-sm sm:text-base flex-shrink-0"
          aria-label="Show filters"
        >
          <svg
            className="w-4 h-4 sm:w-5 sm:h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2a1 1 0 01-.293.707l-6.414 6.414A1 1 0 0013 13.414V19a1 1 0 01-1.447.894l-4-2A1 1 0 017 17v-3.586a1 1 0 00-.293-.707L3.293 6.707A1 1 0 013 6V4z"
            />
          </svg>
          <span className="hidden sm:inline">Search and filter</span>
        </button>
      </div>
      {/* FilterPanel as dropdown/modal */}
      {showFilters && (
        <div className="absolute z-50 mt-2 left-0 w-full max-w-xs sm:max-w-sm">
          <FilterPanel
            filters={filters}
            onFilterChange={onFilterChange}
            onClearFilters={onClearFilters}
            isMobile={false}
            isVisible={showFilters}
            onClose={() => setShowFilters(false)}
          />
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="p-2 sm:p-3 lg:p-4 bg-red-50 border-l-4 border-red-400 text-red-700 flex-shrink-0">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg
                className="h-4 w-4 sm:h-5 sm:w-5 text-red-400"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-2 sm:ml-3">
              <p className="text-xs sm:text-sm">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Chat Log Items */}
      <div className="flex-1 overflow-y-auto bg-pl_bg_light_4">
        {loading && (
          <div className="p-3 sm:p-4 text-center">
            <div className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-4 w-4 sm:h-5 sm:w-5 lg:h-6 lg:w-6 border-b-2 border-pl_prim_color_1"></div>
              <span className="ml-2 text-pl_txt_light_2 text-xs sm:text-sm lg:text-base">
                Loading logs...
              </span>
            </div>
          </div>
        )}
        
        {!loading && filteredLogs.length > 0 && (
          <>
            {/* Search Results Counter */}
            {filters.search && filters.search.trim() !== '' && (
              <div className="p-3 sm:p-4 bg-pl_bg_light_5 border-b border-pl_border_light_1 mx-2 sm:mx-3 mb-2 sm:mb-4 rounded-10">
                <div className="text-sm text-pl_txt_light_1">
                  <span className="font-semibold text-pl_prim_color_1">
                    {filteredLogs.length} result{filteredLogs.length !== 1 ? 's' : ''}
                  </span>
                  {' '}found for "{filters.search}"
                </div>
              </div>
            )}
            
            {filteredLogs.map((log) => (
              <div
                key={log._id}
                onClick={() => {
                  setSelectedId(log._id);
                  onSelect(log);
                }}
                className={`mb-2 sm:mb-4 mx-2 sm:mx-3 cursor-pointer border rounded-10 bg-pl_bg_light_3 hover:bg-pl_bg_light_5 transition-all duration-200 active:scale-95 ${
                  selectedId === log._id
                    ? "border-pl_prim_color_1 bg-pl_bg_light_5 shadow-md"
                    : "border-pl_border_light_1 hover:shadow-sm"
                }`}
              >
                <div className="p-3 sm:p-4">
                  <div className="flex items-center justify-between text-xs sm:text-sm text-pl_txt_light_2 mb-2">
                    <span className="truncate">
                      {new Date(
                        log.timestamp?.$date || log.timestamp
                      ).toLocaleString()}
                    </span>
                    <span
                      className={`px-1.5 sm:px-2 py-0.5 rounded text-xs font-medium flex-shrink-0 ml-2 ${
                        log.status === "success"
                          ? "bg-green-100 text-green-600"
                          : "bg-red-100 text-red-600"
                      }`}
                    >
                      {log.status}
                    </span>
                  </div>
                  <div className="text-xs sm:text-sm text-pl_txt_light_1 font-medium leading-relaxed">
                    {getDisplayTextWithHighlight(log.original_query || "", 60)}
                  </div>
                </div>
              </div>
            ))}
          </>
        )}
        
        {!loading && filteredLogs.length === 0 && !error && (
          <div className="p-4 sm:p-6 lg:p-8 text-center text-pl_txt_light_2">
            <div className="flex flex-col items-center">
              <svg
                className="h-6 w-6 sm:h-8 sm:w-8 lg:h-12 lg:w-12 text-pl_txt_light_3 mb-2"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              <p className="text-xs sm:text-sm">
                {filters.search ? 'No chat logs match your search' : 'No chat logs found.'}
              </p>
              <p className="text-xs text-pl_txt_light_3 mt-1">
                Try adjusting your filters
              </p>
            </div>
          </div>
        )}
        
        {/* Load More Button */}
        {!loading && filteredLogs.length > 0 && hasMore && (
          <div className="p-3 sm:p-4 text-center flex-shrink-0">
            <button
              onClick={handleLoadMore}
              disabled={loadingMore}
              className="w-full max-w-xs mx-auto px-4 sm:px-5 py-2 sm:py-3 bg-gradient-to-r from-pl_prim_color_1 to-pl_prim_color_2 text-pl_bg_light_3 rounded-xl text-sm sm:text-base font-semibold shadow-md hover:from-pl_prim_color_2 hover:to-pl_prim_color_1 disabled:bg-pl_txt_light_3 disabled:cursor-not-allowed transition-colors"
            >
              {loadingMore ? "Loading..." : "Load More"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default ChatLogList;
